/**
 * 🧭 AdminCategoryNav — Navigation admin premium par catégories métier
 *
 * Remplace le menu plat de ~20 onglets par 6 catégories collapsibles
 * (validées produit) :
 *   • Pédagogie & IA
 *   • Coachs & Utilisateurs
 *   • Business & Acquisition
 *   • Finance & Facturation
 *   • Affiliations & Croissance
 *   • Système & Audit
 *
 * Préservé strictement :
 *   - Toutes les routes (`navigate(...)`) inchangées
 *   - Tous les `data-testid` existants conservés sur chaque entrée
 *   - L'état actif (`activeTab`) gère les onglets internes (pillars, modules, etc.)
 *   - Les permissions ne sont pas touchées (composant purement présentationnel)
 *
 * Design : ivoire / or / charbon clair. Aucun fond noir. Mobile premium :
 * accordions verticaux ; Desktop : barre horizontale + dropdowns au clic.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const T = {
  ivory: '#FBF9F3',
  ivoryDeep: '#FFFCEC',
  card: '#FFFFFF',
  charcoal: '#2A2319',
  ink: '#1C1147',
  text: '#1F2937',
  muted: '#6B7280',
  border: '#E8E2D2',
  borderSoft: '#F1ECDC',
  gold: '#D4AF37',
  goldDeep: '#92710A',
};

function CategoryDropdown({ category, currentTab, isOpen, onToggle, onClose }) {
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onClose]);

  // ESC closes
  useEffect(() => {
    if (!isOpen) return;
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [isOpen, onClose]);

  const isActive = category.items.some((it) => it.activeKey && it.activeKey === currentTab);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={onToggle}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        data-testid={`admin-cat-trigger-${category.id}`}
        className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-full transition-all whitespace-nowrap"
        style={{
          background: isActive || isOpen ? T.ivoryDeep : 'transparent',
          color: isActive || isOpen ? T.charcoal : T.text,
          border: `1px solid ${isActive || isOpen ? T.goldDeep : T.border}`,
        }}
      >
        <category.icon size={15} strokeWidth={1.7} style={{ color: T.goldDeep }} />
        <span style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 16, letterSpacing: '0.01em' }}>
          {category.label}
        </span>
        <ChevronDown
          size={14}
          strokeWidth={2}
          style={{
            color: T.muted,
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
      </button>

      {isOpen && (
        <div
          role="menu"
          data-testid={`admin-cat-menu-${category.id}`}
          className="absolute left-0 mt-2 z-50 min-w-[260px] rounded-xl shadow-lg overflow-hidden"
          style={{ background: T.card, border: `1px solid ${T.border}` }}
        >
          {category.items.map((it) => {
            const itemActive = it.activeKey && currentTab === it.activeKey;
            const className = "w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-[#FAF7EE] transition-colors";
            const style = {
              background: itemActive ? T.ivoryDeep : 'transparent',
              borderLeft: itemActive ? `3px solid ${T.goldDeep}` : '3px solid transparent',
              color: T.charcoal,
              textDecoration: 'none',
            };
            const inner = (
              <>
                {it.icon && <it.icon size={14} strokeWidth={1.7} style={{ color: T.goldDeep, flexShrink: 0 }} />}
                <span className="flex-1">{it.label}</span>
                {it.badge != null && (
                  <span className="text-[11px] tabular-nums px-2 py-0.5 rounded-full" style={{ background: T.borderSoft, color: T.muted }}>
                    {it.badge}
                  </span>
                )}
              </>
            );
            // Item de navigation (route) → <Link> natif (anchor) pour fiabilité maximale
            if (it.to) {
              return (
                <Link
                  key={it.id}
                  to={it.to}
                  role="menuitem"
                  data-testid={it.testid}
                  onClick={() => onClose()}
                  className={className}
                  style={style}
                >
                  {inner}
                </Link>
              );
            }
            // Item d'onglet interne (setActiveTab) → button
            return (
              <button
                key={it.id}
                type="button"
                role="menuitem"
                data-testid={it.testid}
                onClick={() => { it.onClick(); onClose(); }}
                className={className}
                style={style}
              >
                {inner}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MobileAccordion({ category, currentTab, isOpen, onToggle, onItemClick, onNavigate }) {
  const isActive = category.items.some((it) => it.activeKey && it.activeKey === currentTab);
  return (
    <div className="border-b" style={{ borderColor: T.borderSoft }}>
      <button
        type="button"
        onClick={onToggle}
        data-testid={`admin-cat-mtrigger-${category.id}`}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        style={{ background: isActive ? T.ivoryDeep : T.card, touchAction: 'manipulation' }}
      >
        <span className="flex items-center gap-2.5">
          <category.icon size={16} strokeWidth={1.7} style={{ color: T.goldDeep }} />
          <span className="text-[15px]" style={{ color: T.charcoal, fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
            {category.label}
          </span>
        </span>
        <ChevronDown
          size={16}
          style={{
            color: T.muted,
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 180ms ease',
          }}
        />
      </button>
      {isOpen && (
        <div data-testid={`admin-cat-mmenu-${category.id}`}>
          {category.items.map((it) => {
            const itemActive = it.activeKey && currentTab === it.activeKey;
            const className = "w-full flex items-center gap-3 px-6 py-2.5 text-left text-sm";
            const style = {
              background: itemActive ? T.ivoryDeep : T.card,
              color: T.charcoal,
              borderLeft: itemActive ? `3px solid ${T.goldDeep}` : '3px solid transparent',
              textDecoration: 'none',
              // touch-action: manipulation désactive le délai 300ms et les optimisations
              // qui pouvaient avorter le click sur mobile Android Chrome.
              touchAction: 'manipulation',
              cursor: 'pointer',
            };
            const inner = (
              <>
                {it.icon && <it.icon size={13} strokeWidth={1.7} style={{ color: T.goldDeep, flexShrink: 0, pointerEvents: 'none' }} />}
                <span className="flex-1" style={{ pointerEvents: 'none' }}>{it.label}</span>
                {it.badge != null && (
                  <span className="text-[10px] tabular-nums px-1.5 py-0.5 rounded-full" style={{ background: T.borderSoft, color: T.muted, pointerEvents: 'none' }}>
                    {it.badge}
                  </span>
                )}
              </>
            );
            const finalize = () => {
              if (onItemClick) onItemClick();
              if (typeof window !== 'undefined') {
                setTimeout(() => {
                  try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {
                    window.scrollTo(0, 0);
                  }
                }, 0);
              }
            };
            // 🔑 FIX MOBILE — utilise pointerup (couvre touch ET souris nativement,
            // et est dispatché AVANT le click) pour garantir le déclenchement de la
            // navigation même si le browser mobile (Android Chrome, iOS Safari) avorte
            // le click event natif après le touchend.
            // Le ref `nav-fired` empêche le double-trigger via le click qui suivrait.
            const handlePointerUp = (e, action) => {
              // Ne pas réagir aux clics droits / molette
              if (e.button !== undefined && e.button !== 0) return;
              // Marqueur sur l'élément pour suppress le click qui peut suivre
              if (e.currentTarget) e.currentTarget.dataset.navFired = '1';
              e.preventDefault();
              e.stopPropagation();
              action();
              finalize();
            };
            const handleClickFallback = (e, action) => {
              // Si pointerup a déjà fait le job, on no-op le click
              if (e.currentTarget && e.currentTarget.dataset.navFired === '1') {
                e.currentTarget.dataset.navFired = '';
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              e.preventDefault();
              e.stopPropagation();
              action();
              finalize();
            };
            // Item de navigation (route) → <Link> natif avec pointerup + click fallback.
            if (it.to) {
              const navAction = () => {
                if (onNavigate) onNavigate(it.to);
              };
              return (
                <Link
                  key={it.id}
                  to={it.to}
                  data-testid={it.testid + '-mobile'}
                  onPointerUp={(e) => handlePointerUp(e, navAction)}
                  onClick={(e) => handleClickFallback(e, navAction)}
                  className={className}
                  style={style}
                >
                  {inner}
                </Link>
              );
            }
            // Item d'onglet interne (setActiveTab) → button
            return (
              <button
                key={it.id}
                type="button"
                data-testid={it.testid + '-mobile'}
                onPointerUp={(e) => handlePointerUp(e, it.onClick)}
                onClick={(e) => handleClickFallback(e, it.onClick)}
                className={className}
                style={style}
              >
                {inner}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AdminCategoryNav({ categories, currentTab }) {
  const [openId, setOpenId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const toggle = useCallback((id) => {
    setOpenId((cur) => (cur === id ? null : id));
  }, []);

  // Auto-close des menus quand la route change (sécurité supplémentaire :
  // si l'utilisateur navigue via back/forward navigateur ou deep-link,
  // les accordéons mobile ouverts se referment automatiquement).
  useEffect(() => {
    setOpenId(null);
  }, [location.pathname]);

  return (
    <>
      {/* Desktop : barre horizontale + dropdowns */}
      <div
        className="hidden md:block sticky top-0 z-30"
        style={{ background: T.ivory, borderBottom: `1px solid ${T.border}` }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3">
          <div className="flex flex-wrap items-center gap-2">
            {categories.map((cat) => (
              <CategoryDropdown
                key={cat.id}
                category={cat}
                currentTab={currentTab}
                isOpen={openId === cat.id}
                onToggle={() => toggle(cat.id)}
                onClose={() => setOpenId(null)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Mobile : accordéon vertical */}
      <div
        className="md:hidden sticky top-0 z-30"
        style={{ background: T.card, borderBottom: `1px solid ${T.border}` }}
      >
        {categories.map((cat) => (
          <MobileAccordion
            key={cat.id}
            category={cat}
            currentTab={currentTab}
            isOpen={openId === cat.id}
            onToggle={() => toggle(cat.id)}
            onItemClick={() => setOpenId(null)}
            onNavigate={(to) => navigate(to)}
          />
        ))}
      </div>
    </>
  );
}
